import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_PROFESSIONAL_PROFILE, fetchProfessionalProfile } from '~/actions';
import { useLoading } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { IJob, IUser } from '~/models';

const ACTIONS = [FETCH_PROFESSIONAL_PROFILE];

/**
 * The full profile of the professional who worked a completed shift.
 *
 * The shift payload's `offer_final_reciever` is **not** enough for the profile
 * blocks: it carries the identity fields the legacy summary showed (name, photo,
 * favourite flag, licence, address) but not `professional_info.education`, which
 * is where specialties, procedures and the achievement badges live. That is why
 * the legacy screen linked out to a separate Professional Profile route instead
 * of rendering them inline — and why this page, which renders them inline, has to
 * fetch what that route fetches.
 *
 * Deliberately the same redux action the Professional Profile screen dispatches,
 * so both surfaces read one cached professional and cannot disagree. The
 * profession/state arguments come off the shift rather than the professional:
 * a professional licensed in several roles has one education map per profession,
 * and the shift decides which one the office is owed.
 *
 * Returns `professional: null` until the fetch lands. Callers render the page's
 * chrome regardless — the shift summary, the receipt and the topper's identity
 * do not depend on this — so there is no full-page spinner here.
 */
export function useCompletedProfessional(job: IJob): {
  professional: IUser | null;
  isLoading: boolean;
} {
  const dispatch = useDispatch();
  const { isLoading } = useLoading(ACTIONS);

  const professionalId = job.offer_final_reciever?.id;
  const officeId = job.offer_owner?.id;
  const { profession, subProfession, state } = job;

  const professional = useAppSelector((store) => store.user.professionalProfile) as IUser | null;

  useEffect(() => {
    if (!professionalId) {
      return;
    }

    dispatch(
      fetchProfessionalProfile(professionalId, officeId, profession, subProfession, state),
    );
  }, [dispatch, professionalId, officeId, profession, subProfession, state]);

  /*
   * The store is shared with the Professional Profile screen, so it can still be
   * holding whoever was viewed last. Serving that profile under this shift's
   * professional would attribute one person's credentials to another, so it is
   * withheld until the id matches.
   */
  const isThisProfessional = Boolean(professionalId) && professional?.id === professionalId;

  return {
    professional: isThisProfessional ? professional : null,
    isLoading,
  };
}
